type LogLevel = "debug" | "info" | "warn" | "error";

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Read env without assuming Node's `process` exists.
 * Client bundles (office WebRTC → logger) run in the browser where `process`
 * is undefined — bare `process.env` throws ReferenceError at module load.
 */
function env(key: string): string | undefined {
  if (typeof process === "undefined") return undefined;
  return process.env[key];
}

const minLevel: LogLevel =
  (env("LOG_LEVEL") as LogLevel | undefined) ||
  (env("NODE_ENV") === "production" ? "info" : "debug");

function emit(level: LogLevel, message: string, data?: Record<string, unknown>) {
  if (LOG_LEVELS[level] < LOG_LEVELS[minLevel]) return;

  const entry = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...data,
  };

  const line = JSON.stringify(entry);

  if (level === "error") {
    console.error(line);
  } else if (level === "warn") {
    console.warn(line);
  } else {
    console.log(line);
  }
}

export const logger = {
  debug: (msg: string, data?: Record<string, unknown>) => emit("debug", msg, data),
  info: (msg: string, data?: Record<string, unknown>) => emit("info", msg, data),
  warn: (msg: string, data?: Record<string, unknown>) => emit("warn", msg, data),
  error: (msg: string, data?: Record<string, unknown>) => emit("error", msg, data),

  /** Log an HTTP request with duration */
  request(req: Request, status: number, durationMs: number, extra?: Record<string, unknown>) {
    const url = new URL(req.url);
    emit(status >= 500 ? "error" : status >= 400 ? "warn" : "info", "http_request", {
      method: req.method,
      path: url.pathname,
      status,
      durationMs: Math.round(durationMs),
      userAgent: req.headers.get("user-agent") ?? undefined,
      ip: req.headers.get("x-forwarded-for") ?? undefined,
      ...extra,
    });
  },
};
